import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import {
  useBrandAssets,
  useFormCollectionFields,
  useSubmitForm,
  useGoogleJwt,
  useApplePass,
} from '../hooks/useApi';
import { downloadFile } from '../utils/helpers';
import { FormData } from '../types';
import Header from '../components/Header';
import { LoadingScreen } from '../components/LoadingSpinner';
import FormFields from '../components/FormFields';
import PassCard from '../components/PassCard';

type AppStage = 'loading' | 'form' | 'card' | 'error';

const PassIssuer: React.FC = () => {
  const { brandId, userId } = useParams<{ brandId: string; userId?: string }>();
  const { state, dispatch } = useApp();
  const [stage, setStage] = useState<AppStage>('loading');
  const [formCollectionId, setFormCollectionId] = useState<string | null>(null);
  const [showGooglePayButton, setShowGooglePayButton] = useState(false);

  // API hooks
  const { data: brandAssets, error: brandError } = useBrandAssets(brandId || null);
  const { data: formFields, error: formError } = useFormCollectionFields(brandId || null);
  const submitFormMutation = useSubmitForm();
  const googleJwtMutation = useGoogleJwt();
  const applePassMutation = useApplePass();

  // Initialize app state
  useEffect(() => {
    if (brandId) {
      dispatch({ type: 'SET_BRAND_ID', payload: brandId });
    }
    if (userId) {
      dispatch({ type: 'SET_USER_ID', payload: userId });
    }
  }, [brandId, userId, dispatch]);

  // Handle brand assets
  useEffect(() => {
    if (brandAssets) {
      dispatch({ type: 'SET_BRAND_ASSETS', payload: brandAssets });
    }
    if (brandError) {
      dispatch({ type: 'SET_ERROR', payload: 'Erreur lors du chargement des assets' });
      setStage('error');
    }
  }, [brandAssets, brandError, dispatch]);

  // Handle form fields
  useEffect(() => {
    if (formFields) {
      dispatch({ type: 'SET_FORM_FIELDS', payload: formFields });

      // Determine if we need to show form or go directly to card
      if (formFields.isEnabled) {
        setStage('form');
      } else {
        setStage('card');
      }
    }
    if (formError) {
      dispatch({ type: 'SET_ERROR', payload: 'Erreur lors du chargement du formulaire' });
      setStage('error');
    }
  }, [formFields, formError, dispatch]);

  // Handle form submission
  const handleFormSubmit = async (formData: FormData) => {
    if (!brandId) return;

    try {
      const dataToSubmit = {
        ...formData,
        ...(userId && { userId }),
      };

      const response = await submitFormMutation.mutateAsync({
        brandId,
        data: dataToSubmit,
      });

      setFormCollectionId(response.id);
      setStage('card');
    } catch (error) {
      console.error('Form submission error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Erreur lors de la soumission du formulaire' });
    }
  };

  // Handle wallet addition
  const handleAddToWallet = async () => {
    if (!brandId) return;

    try {
      if (state.platform === 'apple') {
        const fileUrl = await applePassMutation.mutateAsync({
          brandId,
          formCollectionId: formCollectionId || undefined,
        });
        downloadFile(fileUrl, 'pass.pkpass');
      } else {
        const jwt = await googleJwtMutation.mutateAsync({
          brandId,
          formCollectionId: formCollectionId || undefined,
        });

        // Render Google Pay button
        const renderGooglePayButton = () => {
          // First, show the container
          setShowGooglePayButton(true);

          // Wait for the container to be in the DOM
          setTimeout(() => {
            if (window.gapi && window.gapi.savetoandroidpay) {
              try {
                console.log('Rendering Google Pay button with JWT:', jwt);

                // Clear and render the Google Pay button
                const buttonContainer = document.getElementById('google-pay-button');
                if (buttonContainer) {
                  buttonContainer.innerHTML = ''; // Clear existing content

                  // Render Google Pay button
                  window.gapi.savetoandroidpay.render('google-pay-button', {
                    jwt: jwt,
                  });

                  console.log('Google Pay button rendered successfully');
                } else {
                  console.error('Google Pay button container not found');
                }
              } catch (error) {
                console.error('Error rendering Google Pay button:', error);
                // Show fallback message
                const buttonContainer = document.getElementById('google-pay-button');
                if (buttonContainer) {
                  buttonContainer.innerHTML = `
                    <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-sm mx-auto">
                      <p class="text-sm">Erreur lors du chargement du bouton Google Pay</p>
                      <p class="text-xs mt-1">Veuillez réessayer ou contacter le support</p>
                    </div>
                  `;
                }
              }
            } else {
              console.error('Google Pay API not loaded');
              // Show loading message and retry
              const buttonContainer = document.getElementById('google-pay-button');
              if (buttonContainer) {
                buttonContainer.innerHTML = `
                  <div class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded max-w-sm mx-auto">
                    <p class="text-sm">Chargement du bouton Google Pay...</p>
                  </div>
                `;
              }
              // Retry after a delay
              setTimeout(() => renderGooglePayButton(), 1000);
            }
          }, 100);
        };

        setTimeout(() => renderGooglePayButton(), 100);
      }
    } catch (error) {
      console.error('Wallet addition error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Erreur lors de l\'ajout à votre wallet' });
    }
  };

  // Loading state
  if (stage === 'loading' || (!brandAssets && !brandError)) {
    return <LoadingScreen text="Chargement de votre carte..." />;
  }

  // Error state
  if (stage === 'error' || state.error) {
    return (
      <div className="center-safe bg-gray-50 container-safe landscape-compact ultra-compact">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-[Agency] text-gray-900 mb-4 responsive-text">
            Une erreur s'est produite
          </h1>
          <p className="text-gray-600 mb-6 font-[DMSans] text-sm-responsive">
            {state.error || 'Nous ne pouvons pas charger votre carte pour le moment.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-yellow-400 hover:bg-yellow-500 text-white font-medium py-2 px-4 rounded-lg transition-colors touch-target"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const isFormLoading = submitFormMutation.isLoading;
  const isWalletLoading = googleJwtMutation.isLoading || applePassMutation.isLoading;

  return (
    <div className="h-screen-safe w-screen-safe bg-gray-50 no-overflow-x scroll-safe">
      <Header
        logoUrl={brandAssets?.logoUrl}
        title={brandAssets?.title}
        isVisible={stage === 'card'}
      />

      <main className="h-full w-full mobile-height landscape-compact ultra-compact">
        <AnimatePresence mode="wait">
          {stage === 'form' && formFields && (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.5 }}
              className="h-full container-safe"
            >
              <FormFields
                fields={formFields}
                onSubmit={handleFormSubmit}
                isLoading={isFormLoading}
                brandTitle={brandAssets?.title}
              />
            </motion.div>
          )}

          {stage === 'card' && (
            <motion.div
              key="card"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="h-full container-safe"
            >
              <PassCard
                platform={state.platform}
                brandTitle={brandAssets?.title || 'Walky'}
                bannerUrl={brandAssets?.bannerUrl || undefined}
                onAddToWallet={handleAddToWallet}
                isLoading={isWalletLoading}
                hideButton={state.platform === 'google' && showGooglePayButton}
                showGooglePayContainer={state.platform === 'google' && showGooglePayButton}
              />

            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default PassIssuer;
